import React, { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon } from "lucide-react";
import { volumes } from "../../data/volumes";
import { SectionHeading } from "../ui/SectionHeading";
import { CoverCrop } from "../ui/CoverCrop";
export function VolumeExplorer() {
  const [active, setActive] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const v = volumes[active];
  const total = volumes.length;
  const select = (i: number, focus = false) => {
    const next = (i + total) % total;
    setActive(next);
    const tab = tabRefs.current[next];
    if (focus) tab?.focus();
    tab?.scrollIntoView({
      block: "nearest",
      inline: "nearest"
    });
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowRight") select(active + 1, true);else if (e.key === "ArrowLeft") select(active - 1, true);else if (e.key === "Home") select(0, true);else if (e.key === "End") select(total - 1, true);else return;
    e.preventDefault();
  };
  return <section id="tomos" aria-labelledby="tomos-title" className="scroll-mt-20 bg-crema/60 py-20 md:py-28" data-id="element-258" data-mp="epb20eka59mh">
      <div className="mx-auto max-w-6xl px-4 md:px-6" data-id="element-259" data-mp="9yb8ljadd9oq">
        <SectionHeading id="tomos-title" eyebrow="Los siete tomos" title="Un recorrido de cero a N4, tomo por tomo" intro="Cada tomo resuelve una etapa concreta. Elegí uno para ver qué trabaja y cuándo te conviene usarlo." data-id="element-260" data-mp="9wrqdn55kokb" />
        <p className="mt-5 text-center" data-id="element-261" data-mp="3s3617d84kieh">
          <span className="inline-flex rounded-full bg-salvia-light px-3.5 py-1.5 text-sm font-semibold text-salvia-deep" data-id="element-262" data-mp="3s6ab8r89u8d2">
            Incluidos en Base, Pro y Completo
          </span>
        </p>
        <div role="tablist" aria-label="Tomos del megapack" onKeyDown={onKeyDown} className="no-scrollbar -mx-4 mt-10 flex snap-x gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:justify-center md:px-0" data-id="element-263" data-mp="3i84fysgz00h6">
          {volumes.map((vol, i) => {
          const selected = i === active;
          return <button key={vol.number} ref={el => tabRefs.current[i] = el} role="tab" id={`tomo-tab-${i}`} aria-selected={selected} aria-controls="tomo-panel" tabIndex={selected ? 0 : -1} onClick={() => select(i)} className={`shrink-0 snap-start whitespace-nowrap rounded-full border px-4 py-2 text-sm font-semibold transition-[background-color,border-color,color] duration-150 ease-out ${selected ? "border-pizarra-deep bg-pizarra-deep text-marfil" : "border-pizarra/20 bg-papel text-pizarra hover:border-pizarra/50"}`} data-id="element-264" data-mp="3fmn72hn6cw7x" data-mpi={i}>
                Tomo {vol.number}
              </button>;
        })}
        </div>
        <div id="tomo-panel" role="tabpanel" aria-labelledby={`tomo-tab-${active}`} className="mt-8 overflow-hidden rounded-[28px] border border-pizarra/10 bg-papel p-5 md:p-10" data-id="element-265" data-mp="2d4s4c2uev0dx">
          <AnimatePresence mode="wait" initial={false} data-id="element-266" data-mp="2fmban8l73jke">
            <motion.div key={v.number} initial={{
            opacity: 0,
            x: 12
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -12
          }} transition={{
            duration: 0.18,
            ease: [0.23, 1, 0.32, 1]
          }} className="grid gap-8 md:grid-cols-[minmax(0,300px)_1fr] md:gap-12" data-id="element-267" data-mp="2fjp338yb1426">
              <div className="mx-auto w-full max-w-[260px] md:max-w-none" data-id="element-268" data-mp="1wnz0bjijs3n8">
                <CoverCrop crop={v.crop} alt={`Portada del Tomo ${v.number}: ${v.title}`} className="rounded-md shadow-[0_24px_40px_-24px_rgba(34,52,62,0.55)]" data-id="element-269" data-mp="1rvnphlm9qxfw" />
              </div>
              <div className="min-w-0" data-id="element-270" data-mp="z79r34r9pz3t">
                <p className="text-sm font-semibold text-terracota-deep" data-id="element-271" data-mp="2geprl6qyf4t">
                  Tomo {v.number} de {total} · {v.stage}
                </p>
                <h3 className="mt-2 font-serif text-3xl font-medium leading-tight text-pizarra-deep md:text-4xl" data-id="element-272" data-mp="6i9lma5ws0b7">
                  {v.title}
                </h3>
                <p className="mt-1 text-lg text-pizarra-soft" data-id="element-273" data-mp="g7t7loox4aaa">{v.subtitle}</p>
                <dl className="mt-7 grid gap-6 sm:grid-cols-2" data-id="element-274" data-mp="l6mjxdsgxv8v">
                  <div data-id="element-275" data-mp="l6cw9wuwc06c">
                    <dt className="text-sm font-semibold text-pizarra-deep" data-id="element-276" data-mp="q5pge9udut58">Te ayuda cuando…</dt>
                    <dd className="mt-1.5 leading-relaxed text-pizarra" data-id="element-277" data-mp="q7k0jlxtyh89">{v.problem}</dd>
                  </div>
                  <div data-id="element-278" data-mp="z65ibevh9s7z">
                    <dt className="text-sm font-semibold text-pizarra-deep" data-id="element-279" data-mp="z6nob37xzkt3">Qué ganás</dt>
                    <dd className="mt-1.5 leading-relaxed text-pizarra" data-id="element-280" data-mp="2vz7ejwivzvne">{v.benefit}</dd>
                  </div>
                </dl>
                <div className="mt-7 border-t border-pizarra/10 pt-6" data-id="element-281" data-mp="30yai1cj7xtt9">
                  <p className="text-sm font-semibold text-pizarra-deep" data-id="element-282" data-mp="2tej2nuy902j3">Temas principales</p>
                  <ul className="mt-3 flex flex-wrap gap-2" data-id="element-283" data-mp="2on14tfriui2c">
                    {v.topics.map(t => <li key={t} className="flex items-center gap-1.5 rounded-full bg-crema px-3 py-1.5 text-sm text-pizarra-deep" data-id="element-284" data-mp="2jnoicw4kbd0a">
                        <CheckIcon className="h-3.5 w-3.5 text-salvia-deep" aria-hidden="true" data-id="element-285" data-mp="2er6edyh23ip0" />
                        {t}
                      </li>)}
                  </ul>
                </div>
                <p className="mt-6 rounded-2xl bg-sakura-light/70 px-5 py-4 leading-relaxed text-pizarra-deep" data-id="element-286" data-mp="37bxzvluqtelm">
                  <span className="font-semibold" data-id="element-287" data-mp="2ydcivv4x67my">En la práctica: </span>
                  {v.example}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>
          <div className="mt-8 flex items-center justify-between border-t border-pizarra/10 pt-5" data-id="element-288" data-mp="1qxfwat6wo8gf">
            <button type="button" onClick={() => select(active - 1)} className="inline-flex items-center gap-2 rounded-full border border-pizarra/20 px-4 py-2 text-sm font-semibold text-pizarra-deep transition-colors duration-150 hover:border-pizarra" aria-label={`Tomo anterior: ${volumes[(active - 1 + total) % total].title}`} data-id="element-289" data-mp="1n0jsannv7p8h">
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-290" data-mp="1ji2uhubpe7iv" />
              <span className="hidden sm:inline" data-id="element-291" data-mp="15na39ymgb2wg">Anterior</span>
            </button>
            <p className="text-sm text-pizarra-soft" aria-hidden="true" data-id="element-292" data-mp="1agd3uqpnbcm5">
              {active + 1} / {total}
            </p>
            <button type="button" onClick={() => select(active + 1)} className="inline-flex items-center gap-2 rounded-full border border-pizarra/20 px-4 py-2 text-sm font-semibold text-pizarra-deep transition-colors duration-150 hover:border-pizarra" aria-label={`Tomo siguiente: ${volumes[(active + 1) % total].title}`} data-id="element-293" data-mp="vokmk96ajohs">
              <span className="hidden sm:inline" data-id="element-294" data-mp="83k0cgeamxo8">Siguiente</span>
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-295" data-mp="358s2a3ncv84" />
            </button>
          </div>
        </div>
      </div>
    </section>;
}